import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { WarningOutlineIcon } from '../../components/svgs';
import { ServiceSignatory, SignatoryCheck } from '../../services/service-signatory';
import styles from './page-scrive-request.module.scss';

type Props = {
  organizationNumber?: string;
  /** Names of the external signees currently selected, in selection order. */
  signeeNames: string[];
};

/** Rule blocks vary hugely between companies; collapse anything longer than this. */
const COLLAPSED_LINE_COUNT = 6;

/** A complete Swedish org number, so a half-filled or missing one never reaches the API. */
const isLookupableOrgNumber = (value?: string): value is string =>
  !!value && /^\d{6}-?\d{4}$/.test(value.trim());

/**
 * "Firmatecknare" — who is registered to sign for the supplier, shown above the external
 * signee list so it is available exactly when a signee is being picked.
 *
 * Deliberately advisory: it never disables the send button. Firmateckning covers the formal
 * registration only, and construction purchasing routinely delegates internally (a purchase
 * manager authorized up to a set amount is not a registered signatory), so a mismatch is a
 * prompt to double-check rather than an error.
 */
const SignatoryInfo: FC<Props> = ({ organizationNumber, signeeNames }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'scriveRequest.signatories.firmateckning' });
  const [check, setCheck] = useState<SignatoryCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Re-checked whenever the signee selection changes; the registry lookup behind it is cached
  // server-side, so only the first call for a company actually reaches SYNA/CreditSafe.
  // The effect depends on this string rather than the array itself — the parent rebuilds the
  // array on every render, which would otherwise re-fire the check on unrelated state changes.
  const signeeKey = signeeNames.join('|');

  // Drop the previous company's answer as soon as the supplier changes, so a refetch can never
  // leave another supplier's firmateckning on screen while it is in flight. Keyed on the org
  // number alone — resetting on every signee edit would flash the loading state instead.
  useEffect(() => {
    setCheck(null);
    setExpanded(false);
    setFailed(false);
  }, [organizationNumber]);

  useEffect(() => {
    if (!isLookupableOrgNumber(organizationNumber)) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await ServiceSignatory.checkSignatories(organizationNumber, signeeNames);
        if (!cancelled) {
          setCheck(result);
          setFailed(false);
        }
      } catch (error) {
        // A failed lookup must not interrupt the contract flow — drop the section instead.
        if (!cancelled) {
          setCheck(null);
          setFailed(true);
        }
        Sentry.captureException(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationNumber, signeeKey]);

  if (!isLookupableOrgNumber(organizationNumber) || failed) return null;
  if (!check && loading) {
    return <p className={styles.firmateckning}>{t('loading', 'Hämtar firmateckning...')}</p>;
  }
  // Nothing useful to say when the tenant has no credit service, or the company was not found.
  if (!check || check.status !== 'ok') return null;

  const { source, hasRule, ruleLines, people, signing } = check;
  const sourceName = source === 'syna' ? 'Syna' : 'Creditsafe';

  const showToggle = ruleLines.length > COLLAPSED_LINE_COUNT;
  const visibleLines = expanded || !showToggle ? ruleLines : ruleLines.slice(0, COLLAPSED_LINE_COUNT);

  const renderStatus = () => {
    if (signing.status === 'none') return null;

    if (signing.status === 'ok') {
      return (
        <p className={`${styles.firmateckningStatus} ${styles.ok}`}>
          {signing.viaCeo
            ? t(
                'status.okViaCeo',
                'Vald avtalstecknare är verkställande direktör och får teckna firman avseende löpande förvaltningsåtgärder.'
              )
            : t('status.ok', 'Valda avtalstecknare motsvarar den registrerade firmateckningen.')}
        </p>
      );
    }

    if (signing.status === 'mismatch') {
      return (
        <div className={`${styles.firmateckningStatus} ${styles.warning}`}>
          <WarningOutlineIcon className={styles.firmateckningWarningIcon} />
          <div>
            <p>
              {hasRule
                ? t(
                    'status.mismatch',
                    'Valda avtalstecknare motsvarar inte den registrerade firmateckningen.'
                  )
                : t(
                    'status.notRegistered',
                    'Valda avtalstecknare finns inte bland de registrerade företrädarna.'
                  )}
            </p>
            {signing.unmatched.length > 0 && (
              <p className={styles.firmateckningMuted}>
                {t('status.unmatched', 'Ingen registrerad företrädare matchar: {{names_list}}.', {
                  names: signing.unmatched,
                })}
              </p>
            )}
            <p className={styles.firmateckningMuted}>
              {t(
                'status.advisory',
                'Kontrollen är informativ och hindrar inte utskick. Firmateckningen visar inte intern delegering, till exempel en inköpschef med rätt att teckna upp till ett visst belopp.'
              )}
            </p>
          </div>
        </div>
      );
    }

    // 'unknown' — we know who is registered but not whether this combination may sign.
    return (
      <p className={`${styles.firmateckningStatus} ${styles.firmateckningMuted}`}>
        {hasRule
          ? t(
              'status.unknownRule',
              'Firmateckningen kunde inte tolkas automatiskt. Läs texten ovan och bedöm själv.'
            )
          : t(
              'status.unknownSource',
              'Firmateckningsregeln finns inte hos {{source}}. Kontrollera själv att valda avtalstecknare får teckna firman.',
              { source: sourceName }
            )}
      </p>
    );
  };

  return (
    <div className={styles.firmateckning}>
      <p className={styles.firmateckningHeading}>
        {/* Without a rule there is no firmateckning to show, only who is registered — say so. */}
        {hasRule
          ? t('heading.rule', 'Firmateckning enligt {{source}}', { source: sourceName })
          : t('heading.representatives', 'Registrerade företrädare enligt {{source}}', {
              source: sourceName,
            })}
      </p>

      {hasRule && ruleLines.length > 0 && (
        <>
          <div className={styles.firmateckningRule}>
            {visibleLines.map((line, index) => (
              <span
                key={`${index}-${line.text}`}
                className={line.indent === 1 ? styles.firmateckningEntry : undefined}
              >
                {line.text}
              </span>
            ))}
          </div>
          {showToggle && (
            <button type="button" className={styles.firmateckningToggle} onClick={() => setExpanded(prev => !prev)}>
              {expanded
                ? t('showLess', 'Visa mindre')
                : t('showMore', 'Visa hela firmateckningen ({{count}} rader)', { count: ruleLines.length })}
            </button>
          )}
        </>
      )}

      {/* Creditsafe exposes the registered representatives but not the signing rule itself. */}
      {!hasRule && people.length > 0 && (
        <div className={styles.firmateckningRule}>
          {/* Keyed by index too: two representatives sharing a name is normal in family firms. */}
          {people.map((person, index) => (
            <span key={`${index}-${person.name}`}>
              {person.roleText ? `${person.name} — ${person.roleText}` : person.name}
            </span>
          ))}
        </div>
      )}

      {renderStatus()}
    </div>
  );
};

export default SignatoryInfo;
